import React, { useState, useMemo } from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { TextField } from '@material-ui/core'
import moment from 'moment'

import FormContainer from '../../form/FormContainer'
import SubmitButton from '../../form/SubmitButton'
import NameField from '../../form/NameField'
import DescriptionField from '../../form/DescriptionField'
import SelectField from '../../form/SelectField'
import DatePickerField from '../../form/DatePickerField'
import SecondaryButton from '../../form/SecondaryButton'

import { registerAction, editAction } from 'actions/SecurityAdminActions'
import { useStateValue } from 'context/store'
import { useFormValidation } from 'utils/customHooks/useFormValidation'
import { formUtils } from 'utils/utils'
import { VALUES, REGEXP, OPERACIONES_GRUPOS } from 'utils/constants'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { useHasOperation } from 'hooks/useHasOperation'

const { FORM_BEHAVIOR, DOMAIN_OPTIONS, STATUS_OPTIONS, PAGING_ORDER_INITIAL_STATE } = VALUES
const { REGISTER, SEARCH, EDIT } = FORM_BEHAVIOR

const SUBMIT_BUTTON_TEXT = {
   [REGISTER]: 'GUARDAR',
   [SEARCH]: 'CONSULTAR',
   [EDIT]: 'EDITAR'
}

// Solo los campos del formulario en modo Registrar o Editar
const INITIAL_GROUP_FORM_VALIDATION = {
   name: false,
   description: false,
   finalDate: false,
   domain: false
}

const { getFieldLabel, notRequired, doesNotMatchRegexp, notEmpty } = formUtils
const isInvalidIf = {
   name: value => notRequired(value) || doesNotMatchRegexp(value, REGEXP.VALIDATION_NAME),
   description: notRequired,
   domain: notRequired,
   roles: notEmpty
}

const internalToApiMapper = formData => ({
   name: formData.name,
   description: formData.description,
   domain: formData.domain,
   rolesId: formData.roles.map(role => role?.id),
   ...(formData?.finalDate ? { finalDate: moment(formData.finalDate).format('YYYY-MM-DD') } : {}),
   ...(formData?.status ? { status: formData.status } : {})
})

function GroupForm({ formState, formBehavior, cleanCallback, setTableData, roles, searchCallback, setOpenPopover }) {
   const isSearch = useMemo(() => formBehavior === SEARCH, [formBehavior])

   const [formData] = formState
   const [isInvalid, setIsInvalid] = useState(INITIAL_GROUP_FORM_VALIDATION)

   const [_, dispatch] = useStateValue()
   const REGISTAR_GRUPO = useHasOperation(OPERACIONES_GRUPOS.REGISTAR)
   const CONSULTAR_GRUPO = useHasOperation(OPERACIONES_GRUPOS.CONSULTAR)

   const { setValue, isAnyFieldInvalid } = useFormValidation(formState, setIsInvalid, isInvalidIf)

   function cleanForm() {
      cleanCallback()
      setIsInvalid(INITIAL_GROUP_FORM_VALIDATION)
   }

   // ------- Register Group ---------
   async function _registerGroup() {
      const payload = internalToApiMapper(formData)
      const url = `${process.env.REACT_APP_GROUPS_API}/groups`
      await registerAction(url, payload, cleanForm, setTableData, dispatch)
      setOpenPopover(false)
   }

   const registerGroup = useApiRequest(_registerGroup, 'Grupos: Registrar')

   // ------- Search Group ---------
   async function searchGroup() {
      const externalFilters = { ...formData }
      if (formData.finalDate) {
         externalFilters.finalDate = moment(formData.finalDate).format('YYYY-MM-DD')
      }
      await searchCallback(externalFilters, PAGING_ORDER_INITIAL_STATE)
      setOpenPopover(false)
   }

   // ------- Edit Group ---------
   async function _editGroup() {
      const payload = internalToApiMapper(formData)
      const url = `${process.env.REACT_APP_GROUPS_API}/groups/${formData.id}`
      await editAction(url, payload, cleanForm, setTableData, dispatch)
      setOpenPopover(false)
   }

   const editGroup = useApiRequest(_editGroup, 'Grupos: Editar')

   // ------- Submit Group ---------
   function onSubmit(event) {
      event.preventDefault()
      if (isSearch) searchGroup()
      else if (!isAnyFieldInvalid()) {
         if (formBehavior === REGISTER) registerGroup()
         else if (formBehavior === EDIT) editGroup()
      }
   }

   return (
      <FormContainer onSubmit={onSubmit}>
         <NameField
            fieldLabel={getFieldLabel(isSearch, 'Nombre del Grupo')}
            formValue={formData?.name}
            fieldInvalid={isInvalid.name}
            handleChange={setValue}
            maxLength={25}
         />
         {!isSearch && (
            <DescriptionField
               fieldLabel={'Descripción del Grupo *'}
               formValue={formData?.description}
               fieldInvalid={isInvalid.description}
               handleChange={setValue}
               maxLength={250}
            />
         )}
         <DatePickerField
            size="small"
            fieldLabel="Vigencia Final"
            formValue={formData?.finalDate || null}
            fieldName="finalDate"
            handleChange={setValue}
            minDate={isSearch ? moment('01-01-1900', 'DD-MM-YYYY') : moment()}
            minDateMessage={`La Vigencia Final no puede ser inferior a la fecha actual: ${moment().format(
               'DD-MM-YYYY'
            )}`}
         />
         <SelectField
            fieldInvalid={isInvalid.domain}
            formValue={formData?.domain}
            handleChange={setValue}
            fieldLabel={getFieldLabel(isSearch, 'Dominio')}
            fieldName="domain"
            errorMessage="El campo es obligatorio."
            items={DOMAIN_OPTIONS}
         />
         {formBehavior !== REGISTER && (
            <SelectField
               fieldInvalid={isInvalid.status}
               formValue={formData?.status}
               handleChange={setValue}
               fieldLabel={'Estado del Grupo'}
               fieldName="status"
               errorMessage="El campo es obligatorio." // Solo en Editar
               items={STATUS_OPTIONS}
            />
         )}
         {!isSearch && (
            <Autocomplete
               style={{ marginTop: 15 }}
               size="small"
               multiple
               name="roles"
               value={formData?.roles}
               onChange={(_, value) => setValue({ target: { name: 'roles', value } })}
               options={roles}
               getOptionLabel={row => row?.name}
               renderInput={params => (
                  <TextField
                     label="Nombre de(los) Rol(es) *"
                     variant="outlined"
                     error={isInvalid.roles}
                     helperText={isInvalid.roles && 'Seleccione al menos un rol válido'}
                     {...params}
                  />
               )}
            />
         )}
         <SecondaryButton text="LIMPIAR" onClick={cleanForm} />
         <SubmitButton 
            text={SUBMIT_BUTTON_TEXT[formBehavior]} 
            disabled={isSearch ? !CONSULTAR_GRUPO : !REGISTAR_GRUPO} 
         />
      </FormContainer>
   )
}

export default GroupForm
